import { CircularProgress } from '@mui/material'
import { DiscussionThread } from './DiscussionThread'
import { PastVersionSection } from './PastVersionSection'
import { useDiscussion } from './hooks/useDiscussion'
import { useDiscussionStateContext } from '../../context/DiscussionContext'
import { intl } from '../../../../../i18n'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import {
  EmptyMessage,
  LoadingContainer,
  ThreadListContainer,
} from './panelStyles'

type VersionTimelineViewProps = {
  projectUuid: string
  versionPageUuid: string
  discussion: ReturnType<typeof useDiscussion>
  currentVersion: VersionReportVersionEntity | undefined
}

export const VersionTimelineView = ({
  projectUuid,
  versionPageUuid,
  discussion,
  currentVersion,
}: VersionTimelineViewProps) => {
  const {
    currentDiscussion,
    pastVersions,
    versionCache,
    expandedVersionUuids,
    isLoadingCurrent,
    loadingVersionUuids,
    toggleVersion,
    refreshCurrent,
  } = discussion
  const { templatePageUuid } = useDiscussionStateContext()

  const getPastVersionThreadCount = (v: VersionReportVersionEntity): number => {
    const cached = versionCache.get(v.uuid)
    if (cached) return cached.discussionThreads.length
    if (!templatePageUuid) return 0
    return v.discussionThreadCountByTemplatePage?.[templatePageUuid] ?? 0
  }

  const currentThreadsSorted = currentDiscussion
    ? [...currentDiscussion.discussionThreads].sort(
        (a, b) => b.createdAt - a.createdAt
      )
    : []

  return (
    <>
      {isLoadingCurrent && !currentDiscussion && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}

      {!isLoadingCurrent &&
        currentDiscussion &&
        currentThreadsSorted.length === 0 && (
          <EmptyMessage>
            {intl.formatMessage({ id: 'discussion.empty' })}
          </EmptyMessage>
        )}

      {currentThreadsSorted.length > 0 && (
        <ThreadListContainer>
          {currentThreadsSorted.map(thread => (
            <DiscussionThread
              key={thread.threadUuid}
              thread={thread}
              projectUuid={projectUuid}
              versionPageUuid={versionPageUuid}
              onRefresh={refreshCurrent}
              readOnly={false}
            />
          ))}
        </ThreadListContainer>
      )}

      {pastVersions.map(v => (
        <PastVersionSection
          key={v.uuid}
          versionName={v.name}
          threadCount={getPastVersionThreadCount(v)}
          discussion={versionCache.get(v.uuid)}
          isExpanded={expandedVersionUuids.has(v.uuid)}
          isLoading={loadingVersionUuids.has(v.uuid)}
          onToggle={() => toggleVersion(v.uuid)}
          projectUuid={projectUuid}
          versionPageUuid={versionPageUuid}
        />
      ))}

      {!currentVersion && !isLoadingCurrent && (
        <EmptyMessage>
          {intl.formatMessage({ id: 'discussion.empty' })}
        </EmptyMessage>
      )}
    </>
  )
}
